import { useMobile } from "@/hooks";
import trackClevertapEvents from "@/utils/trackClevertapEvents";
import {
  Button,
  Eye,
  EyeHide,
  Toast,
} from "@monsterindia/global-ui-components";
import React, { useState } from "react";

import { changePassword } from "@/components/settings/api/networkUtils";
import ModalWithHeaderFooter from "@/components/settings/components/common/ModalWithHeaderFooter";
import {
  changePasswordContent,
  getPasswordFields,
} from "@/components/settings/contents/changePassword";

import {
  checkIsPasswordValid,
  onValidPasswordMessage,
} from "../../utils/validatePassword";

function ChangePassword() {
  const [toast, setToast] = useState({ text: "", type: "", show: false });
  const [showModal, setShowModal] = useState(false);
  const isMobile = useMobile();
  const [password, setPassword] = useState<passwordDataType>({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState<showPasswordDataType>({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [passwordTouched, setPasswordTouched] = useState<showPasswordDataType>({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [showChangePasswordForm, setShowChangePasswordForm] =
    useState<boolean>(false);

  const callChangePassword = async (payload: {}) => {
    const isPasswordValid = getPasswordFields(
      showPassword,
      password,
      passwordTouched,
    ).map((pass) => checkIsPasswordValid(pass, password));
    if (isPasswordValid.every((val) => val)) {
      try {
        const data: any = await changePassword(payload);
        if (data) {
          trackClevertapEvents.eventPush("Settings Updated", {
            updatePassword: "Yes",
          });
        }
        setToast({
          text: data?.successMessage || "Action completed successfully",
          type: "success",
          show: true,
        });
        onClear();
      } catch (err: any) {
        setToast({
          text: err?.response?.data?.errorMessage || "",
          type: "error",
          show: true,
        });
        onClear();
      }
    }
  };
  const onChangePassword = async () => {
    if (showChangePasswordForm) {
      const allPasswordsFilled =
        password.oldPassword &&
        password.newPassword &&
        password.confirmPassword;
      if (!allPasswordsFilled) {
        setToast({
          text: "Please enter all password",
          type: "error",
          show: true,
        });
        return;
      }
      const payload = {
        oldPassword: password?.oldPassword,
        newPassword: password?.newPassword,
        confirmPassword: password?.confirmPassword,
      };
      callChangePassword(payload);
    } else {
      setShowChangePasswordForm(true);
    }
  };

  const onChange = (e: { target: { name: string; value: string } }) => {
    setPassword({ ...password, [e?.target?.name]: e?.target?.value });
  };

  const onBlur = (e: { target: { name: string; value: string } }) => {
    setPasswordTouched({ ...passwordTouched, [e.target.name]: true });
  };

  const onShowPassword = (name: string, value: boolean) => {
    setShowPassword({ ...showPassword, [name]: value });
    setShowModal(true);
  };

  const onClear = () => {
    setPassword({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setShowPassword({
      oldPassword: false,
      newPassword: false,
      confirmPassword: false,
    });
    setPasswordTouched({
      oldPassword: false,
      newPassword: false,
      confirmPassword: false,
    });
    setShowChangePasswordForm(false);
  };

  const closeModel = () => {
    setShowChangePasswordForm(false);
  };

  const showChangePasswordFormJSXForMobile = isMobile && (
    <ModalWithHeaderFooter
      heading="Change Password"
      showModal={showChangePasswordForm}
      closeModal={closeModel}
    >
      {showChangePasswordForm && (
        <div className="flex flex-col items-center gap-2 rounded-xl bg-white">
          {getPasswordFields(showPassword, password, passwordTouched).map(
            (item) => {
              return (
                <div key={item?.name ?? "input"} className="w-full">
                  <label className="mb-1 mt-1 text-sm">
                    {item?.label ?? "Input"}
                  </label>
                  <div className="flex items-center">
                    <input
                      className="w-full rounded border-2 border-solid border-indigo-600 p-2 text-sm"
                      autoComplete="nope"
                      type={item?.type ?? "text"}
                      placeholder={item?.placeholder ?? "Enter value"}
                      name={item?.name ?? ""}
                      value={item?.value ?? ""}
                      onFocus={() => {}}
                      onBlur={(e) => {
                        onBlur(e);
                      }}
                      onChange={(e) => onChange(e)}
                      onKeyDown={() => {}}
                    />
                    {item.type !== "password" ? (
                      <div
                        className="-ml-7 cursor-pointer"
                        onClick={() => onShowPassword(item.name, false)}
                      >
                        <Eye className="size-4 cursor-pointer" />
                      </div>
                    ) : (
                      <div
                        className="-ml-7 cursor-pointer"
                        onClick={() => onShowPassword(item.name, true)}
                      >
                        <EyeHide className="size-4 cursor-pointer" />
                      </div>
                    )}
                  </div>
                  {item.touched && (
                    <label className="mb-1 mt-1 text-[12px] text-red-500">
                      {onValidPasswordMessage(item, password)}
                    </label>
                  )}
                </div>
              );
            },
          )}
          <div className="modalFooterFix modal_footer flex justify-around gap-5 md:justify-end">
            <Button
              onClick={closeModel}
              label="Cancel"
              modifier="tertiary"
              className="w-full whitespace-nowrap md:w-auto"
            />
            <Button
              onClick={onChangePassword}
              label="Update"
              modifier="primary"
              size="lg"
              className="w-full whitespace-nowrap md:w-auto"
            />
          </div>
        </div>
      )}
    </ModalWithHeaderFooter>
  );

  const showChangePasswordFormJSXForDesktop = !isMobile &&
    showChangePasswordForm && (
      <div className="flex flex-col gap-2 rounded-xl bg-white p-2">
        {getPasswordFields(showPassword, password, passwordTouched).map(
          (item) => {
            return (
              <div key={item?.name ?? "input"}>
                <label className="mb-1 mt-1 text-sm">
                  {item?.label ?? "Input"}
                </label>
                <div className="flex items-center">
                  <input
                    className="w-80 rounded border-2 border-solid border-indigo-600 p-2 text-sm"
                    autoComplete="nope"
                    type={item?.type ?? "text"}
                    placeholder={item?.placeholder ?? "Enter value"}
                    name={item?.name ?? ""}
                    value={item?.value ?? ""}
                    onFocus={() => {}}
                    onBlur={(e) => {
                      onBlur(e);
                    }}
                    onChange={(e) => onChange(e)}
                    onKeyDown={() => {}}
                  />
                  {item.type !== "password" ? (
                    <div
                      className="-ml-7 cursor-pointer"
                      onClick={() => onShowPassword(item?.name ?? "", false)}
                    >
                      <Eye className="size-4 cursor-pointer" />
                    </div>
                  ) : (
                    <div
                      className="-ml-7 cursor-pointer"
                      onClick={() => onShowPassword(item?.name ?? "", true)}
                    >
                      <EyeHide className="size-4 cursor-pointer" />
                    </div>
                  )}
                </div>
                {item.touched && (
                  <label className="mb-1 mt-1 text-[12px] text-red-500">
                    {onValidPasswordMessage(item, password)}
                  </label>
                )}
              </div>
            );
          },
        )}
      </div>
    );

  return (
    <>
      <Toast
        timer={3000}
        showToast={toast.show}
        text={toast.text}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
        width={isMobile ? "75" : "50"}
        position="top-center"
      />
      <div className="bg-surface-secondary flex flex-col gap-3 rounded-xl p-3">
        <div className="border-border-moderate border-b py-3 text-base font-semibold">
          {changePasswordContent?.settingsPageHeading ?? "Change Password"}
        </div>
        <div className="text-content-secondary text-sm font-normal">
          {changePasswordContent?.settingsPageSubheading ??
            "Update your password to keep your account secure."}
        </div>
        {showChangePasswordFormJSXForMobile}
        {showChangePasswordFormJSXForDesktop}

        <div
          className={`mt-3 cursor-pointer rounded text-sm font-bold text-[#6e00be] ${showChangePasswordForm && "ml-2 w-40 border border-[#6e00be] p-3 text-center"} `}
          onClick={() => onChangePassword()}
        >
          {changePasswordContent?.settingsPageHeading ?? "Change Password"}
        </div>
      </div>
    </>
  );
}
export default ChangePassword;
